import {firstOf, get, isFilled, pipe} from '@yandex-delivery/utils'
import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react'
import type {DropResult} from 'react-beautiful-dnd'
import {DragDropContext, Draggable, Droppable} from 'react-beautiful-dnd'

import type {DestinationPoint, SourcePoint} from 'src/shared/types'

import {DraggableRouteItem} from '../DraggableRouteItem/DraggableRouteItem'
import * as classes from './DraggableRouteItems.classes'

type Props = {
  source: SourcePoint
  destinations: DestinationPoint[]
  sortOrder?: string[] | null
  onDragEnd: (routeIds: string[]) => void
}

const reorder = (list: string[], startIndex: number, endIndex?: number) => {
  const result = [...list]
  const [removed] = result.splice(startIndex, 1)

  if (removed) {
    result.splice(endIndex ?? startIndex, 0, removed)
  }

  return result
}

export const DraggableRouteItems = React.memo<Props>(function DraggableRouteItems(props) {
  const {source, destinations, sortOrder, onDragEnd} = props
  const ref = useRef<HTMLDivElement>(null)
  const route = useMemo(() => {
    return [source, ...destinations]
  }, [destinations, source])
  const [order, setOrder] = useState<string[]>(route.map(item => item._id.toString()))

  const sortedRouteByOrder = useMemo(
    () => order.map(id => route.find(point => String(get('_id', point)) === id)).filter(isFilled),
    [order, route],
  )

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      const newSortOrder = reorder(order, result.source.index, result.destination?.index)
      setOrder(newSortOrder)
      onDragEnd(newSortOrder)
    },
    [onDragEnd, order],
  )

  // Новый порядок приоритетнее
  useEffect(() => {
    if (sortOrder) {
      setOrder(sortOrder)
    }
  }, [sortOrder])

  useLayoutEffect(() => {
    if (ref.current) {
      //  Контейнер должен быть фиксированной высоты, под все элементы, иначе модалка будет скакать туда-сюда
      ref.current.style.height = [...ref.current.children].reduce((acc, child) => acc + child.clientHeight, 0) + 'px'
    }
  }, [])

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div ref={ref} className={classes.fullWidth}>
        <Droppable droppableId="drag-route">
          {provided => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {sortedRouteByOrder.map((item, index) => {
                const id = get('_id', item)
                const addressName = pipe(item, firstOf(get('address.fullName'), get('address.shortName')))
                const person = pipe(item, get('contact.name'))
                const phone = pipe(item, get('contact.phone'))

                if (index === 0) {
                  return (
                    <DraggableRouteItem key={id} address={addressName} index={index} person={person} phone={phone} />
                  )
                }

                return (
                  <Draggable key={id} draggableId={String(id)} index={index}>
                    {(providedData, snapshot) => (
                      <div
                        ref={providedData.innerRef}
                        {...providedData.draggableProps}
                        {...providedData.dragHandleProps}
                      >
                        <DraggableRouteItem
                          address={addressName}
                          index={index}
                          isDragging={snapshot.isDragging && !snapshot.isDropAnimating}
                          person={person}
                          phone={phone}
                        />
                      </div>
                    )}
                  </Draggable>
                )
              })}
            </div>
          )}
        </Droppable>
      </div>
    </DragDropContext>
  )
})
